/*
 * Adversary Force: schwebende App-Widgets gestalterisch einbinden, ohne eine App zu entfernen
 * (docs/design-manifest.md: Ebenen, „Keine überdeckten Buttons“). Stile: assets/af-layout.css, Abschnitt 8.
 *
 * - Shopify Forms: data-af-forms-open am <shopify-forms-embed>, solange das Formular geöffnet ist
 *   (Schritt im Shadow DOM ungleich „tease“). Die Stile blenden nur den Teaser aus, nie ein offenes
 *   Formular. Gleiche Quelle wie assets/sticky-atc.js (data-current-step).
 * - Feste Regeln statt flächendeckender Kollisionsprüfung (Codex-Zweitprüfung Runde 4, Punkt 5):
 *   1. Dialoge (Body-Klasse overflow-hidden*, Größenhilfe af-dialog-open), Warenkorb-Drawer und
 *      /cart: Chat und Rabatt-Teaser aus (af-layout.css, af-cart.css, Paket 1).
 *   2. Kaufleiste sichtbar: Chat rückt über die Leiste (vorgesehener Versatz
 *      --shopify-chat-activator-offset), Rabatt-Teaser aus (af-product.css).
 *   3. Kaufbereich: Body-Klasse af-chat-yield bzw. af-forms-yield nur, solange der schwebende Knopf
 *      genau über Variantenauswahl, Kaufbuttons oder Benachrichtigungsbutton der Hauptsektion liegt.
 *      Geprüft werden nur diese Container, keine Absätze, Links oder Karten. Gemessen wird die
 *      tatsächliche Position des Knopfs (Shadow DOM der App, bei Shopify Inbox part="activator").
 *      Ausblenden sofort, Einblenden nach kurzer Ruhe. Dauerhaft erreichbarer Kontaktweg, solange der
 *      Chat dort weicht: „Schreib uns“ im Kaufbereich (snippets/buy-buttons.liquid bzw. Größenhilfe).
 *   4. Rabatt-Teaser zusätzlich aus, solange auf Produktseiten Preis, Auswahl oder Kaufbutton im
 *      Sichtfeld liegen (Body-Klasse af-buybox-visible), damit er nicht mit der Kaufaktion konkurriert.
 *   Ein geöffneter Chat (Attribut open) und ein geöffnetes Formular (data-af-forms-open) werden nie
 *   ausgeblendet. Beobachtet wird nur die Hauptsektion des Produkts; nach einem Austausch der Sektion
 *   (Theme-Editor shopify:section:load, Produktwechsel) wird neu angebunden.
 * - Consentmo (Cookie-Banner, offenes Shadow DOM ohne part-Attribute): sichtbarer Tastaturfokus
 *   nach Manifest (Orange auf dunklem Banner, 2 px mit 3 px Abstand), auch für die Schalter, deren
 *   Eingabefelder 0 x 0 px groß sind (Ring dann am zugehörigen Label). Nur ein zusätzliches
 *   Stylesheet über adoptedStyleSheets, Texte und Verhalten der App bleiben unverändert.
 * - Restock Rocket („Benachrichtigen, wenn verfügbar“): Die App hängt ihre Lasche bei der Einstellung
 *   float-right direkt an den body. Sie wird hinter das Produktformular verschoben und als normaler
 *   Button dargestellt (Klasse af-restock). Die App findet den Container weiter über seine Klasse und
 *   entfernt ihn mit Element.remove(), das Verschieben ist für sie unschädlich.
 */
(function () {
  if (window.afWidgetsReady) return;
  window.afWidgetsReady = true;

  /* ---------- Shopify Forms ---------- */
  const watchedForms = new WeakSet();

  function syncForms(embed) {
    const root = embed.shadowRoot;
    const stepElement = root && root.querySelector('[data-current-step]');
    const step = stepElement ? stepElement.dataset.currentStep : '';
    embed.toggleAttribute('data-af-forms-open', !!step && step !== 'tease');
  }

  function watchForms(embed) {
    if (watchedForms.has(embed) || !embed.shadowRoot) return;
    watchedForms.add(embed);
    new MutationObserver(() => syncForms(embed)).observe(embed.shadowRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-current-step'],
    });
    syncForms(embed);
  }

  function scanForms() {
    document.querySelectorAll('shopify-forms-embed').forEach(watchForms);
  }

  /* ---------- Consentmo: sichtbarer Fokus im Cookie-Banner ---------- */
  const consentFocusCss = [
    ':is(button, a[href], [role="button"], [tabindex]:not([tabindex="-1"])):focus-visible {',
    '  outline: 2px solid #FA8500 !important;',
    '  outline-offset: 3px !important;',
    '}',
    'label:has(input:focus-visible) {',
    '  outline: 2px solid #FA8500 !important;',
    '  outline-offset: 3px !important;',
    '}',
  ].join(' ');
  let consentSheet = null;

  function styleConsent() {
    const host = document.querySelector('csm-cookie-consent');
    const root = host && host.shadowRoot;
    if (!root || !('adoptedStyleSheets' in root)) return;
    try {
      if (!consentSheet) {
        consentSheet = new CSSStyleSheet();
        consentSheet.replaceSync(consentFocusCss);
      }
      if (!root.adoptedStyleSheets.includes(consentSheet)) {
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, consentSheet];
      }
    } catch (error) {
      // Ältere Browser ohne konstruierbare Stylesheets: Banner bleibt wie von der App geliefert
    }
  }

  /* ---------- Restock Rocket ---------- */
  const restockSelector =
    '.restock-rocket-button-container-float-right, .restock-rocket-button-container-float-left';

  function mainProductForm() {
    const info = document.querySelector('product-info[id^="MainProduct-"]');
    return info ? info.querySelector('product-form') : null;
  }

  function placeRestock() {
    const form = mainProductForm();
    if (!form) return;
    document.querySelectorAll(restockSelector).forEach((container) => {
      if (!container.classList.contains('af-restock')) container.classList.add('af-restock');
      if (form.nextElementSibling !== container) form.insertAdjacentElement('afterend', container);
    });
  }

  /* ---------- Kaufbereich der Hauptsektion ---------- */
  function mainProductInfo() {
    return document.querySelector('product-info[id^="MainProduct-"]');
  }

  // Container, über denen Chat-Knopf und Rabatt-Teaser nicht liegen dürfen (Regel 3)
  function protectedContainers(info) {
    if (!info) return [];
    return [
      info.querySelector('variant-selects'),
      info.querySelector('product-form .product-form__buttons'),
      info.querySelector('.af-restock'),
    ].filter(Boolean);
  }

  // Container für af-buybox-visible (Regel 4)
  function buyBoxContainers(info) {
    if (!info) return [];
    return [info.querySelector('[id^="price-"]'), info.querySelector('variant-selects'), info.querySelector('product-form')].filter(
      Boolean
    );
  }

  const YIELD_MARGIN = 8; // Sicherheitsabstand um den Knopf
  const SHOW_DELAY = 300; // Ruhezeit vor dem Wiedereinblenden

  function chatActivator() {
    const host = document.querySelector('shopify-chat');
    const runtime = host && host.shadowRoot && host.shadowRoot.querySelector('shopify-agent-runtime');
    const root = runtime && runtime.shadowRoot;
    return root ? root.querySelector('[part~="activator"]') || root.querySelector('.activator') : null;
  }

  function formsTeaser() {
    const embed = document.querySelector('shopify-forms-embed');
    const root = embed && embed.shadowRoot;
    return root ? root.querySelector('[class*="teaserContainer"]') : null;
  }

  // Der Chat-Knopf rückt über die Kaufleiste (af-product.css, --shopify-chat-activator-offset)
  function stickyOffset() {
    if (!document.body.classList.contains('sticky-atc-visible')) return 0;
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-atc-height')) || 0;
  }

  function createYield(name, findWidget, followsStickyBar) {
    const state = {
      inset: null, // Abstand zum rechten und unteren Rand, unabhängig von der Fensterhöhe
      key: '',
      observer: null,
      targets: [],
      hits: new Set(),
      showTimer: null,
    };
    const bodyClass = 'af-' + name + '-yield';

    function setYield(on) {
      if (on) {
        clearTimeout(state.showTimer);
        state.showTimer = null;
        document.body.classList.add(bodyClass);
        return;
      }
      if (!document.body.classList.contains(bodyClass) || state.showTimer) return;
      state.showTimer = setTimeout(() => {
        state.showTimer = null;
        if (state.hits.size === 0) document.body.classList.remove(bodyClass);
      }, SHOW_DELAY);
    }

    // Position nur messen, solange der Knopf zu sehen ist; ausgeblendet gilt die letzte Messung
    function measure() {
      const widget = findWidget();
      if (!widget) return;
      const rect = widget.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const width = document.documentElement.clientWidth;
      const height = window.innerHeight;
      state.inset = {
        right: width - rect.right,
        bottom: height - rect.bottom - (followsStickyBar ? stickyOffset() : 0),
        width: rect.width,
        height: rect.height,
      };
    }

    function disconnect() {
      if (state.observer) state.observer.disconnect();
      state.observer = null;
      state.key = '';
      state.hits.clear();
    }

    function rebuild(targets) {
      if (targets) state.targets = targets;
      if (!state.targets.length) {
        disconnect();
        setYield(false);
        return;
      }
      measure();
      if (!state.inset) return;
      const width = document.documentElement.clientWidth;
      const height = window.innerHeight;
      const bottomInset = state.inset.bottom + (followsStickyBar ? stickyOffset() : 0);
      const right = Math.min(width, width - state.inset.right + YIELD_MARGIN);
      const left = Math.max(0, right - state.inset.width - 2 * YIELD_MARGIN);
      const bottom = Math.min(height, height - bottomInset + YIELD_MARGIN);
      const top = Math.max(0, bottom - state.inset.height - 2 * YIELD_MARGIN);
      const margin = [top, width - right, height - bottom, left].map((value) => -Math.round(value) + 'px').join(' ');
      const key = margin + '|' + state.targets.length;
      if (key === state.key && state.observer && !targets) return;
      disconnect();
      state.key = key;
      state.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const box = entry.boundingClientRect;
            if (entry.isIntersecting && box.width >= 2 && box.height >= 2) state.hits.add(entry.target);
            else state.hits.delete(entry.target);
          });
          state.hits.forEach((element) => {
            if (!element.isConnected) state.hits.delete(element);
          });
          setYield(state.hits.size > 0);
        },
        { rootMargin: margin, threshold: 0 }
      );
      state.targets.forEach((element) => state.observer.observe(element));
    }

    return { rebuild };
  }

  const yields = [createYield('chat', chatActivator, true), createYield('forms', formsTeaser, false)];

  const buyBox = {
    info: null,
    mutationObserver: null,
    visibilityObserver: null,
    visible: new Set(),
    protectedTargets: null,
  };

  function setBuyBoxVisible() {
    buyBox.visible.forEach((element) => {
      if (!element.isConnected) buyBox.visible.delete(element);
    });
    document.body.classList.toggle('af-buybox-visible', buyBox.visible.size > 0);
  }

  // Container neu einsammeln (Dawn ersetzt variant-selects beim Variantenwechsel)
  function collectBuyBox() {
    const info = buyBox.info;
    if (buyBox.visibilityObserver) {
      buyBox.visibilityObserver.disconnect();
      buyBox.visible.clear();
      buyBoxContainers(info).forEach((element) => buyBox.visibilityObserver.observe(element));
    }
    setBuyBoxVisible();

    const targets = protectedContainers(info);
    // Gleiche Knoten: nur neu vermessen
    const previous = buyBox.protectedTargets;
    const same =
      !!previous && targets.length === previous.length && targets.every((element, index) => element === previous[index]);
    buyBox.protectedTargets = targets;
    if (!('IntersectionObserver' in window)) return;
    yields.forEach((item) => (same ? item.rebuild() : item.rebuild(targets)));
  }

  let collectTimer = null;
  function scheduleCollect() {
    clearTimeout(collectTimer);
    collectTimer = setTimeout(collectBuyBox, 150);
  }

  function bindBuyBox() {
    const info = mainProductInfo();
    if (info === buyBox.info && info && info.isConnected) {
      scheduleCollect();
      return;
    }
    if (buyBox.mutationObserver) buyBox.mutationObserver.disconnect();
    if (buyBox.visibilityObserver) buyBox.visibilityObserver.disconnect();
    buyBox.visible.clear();
    buyBox.protectedTargets = null;
    buyBox.info = info;

    if (info && 'IntersectionObserver' in window) {
      buyBox.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) buyBox.visible.add(entry.target);
          else buyBox.visible.delete(entry.target);
        });
        setBuyBoxVisible();
      });
      // Nur die Hauptsektion beobachten, nicht das ganze Dokument
      buyBox.mutationObserver = new MutationObserver(scheduleCollect);
      buyBox.mutationObserver.observe(info, { childList: true, subtree: true });
    } else {
      buyBox.visibilityObserver = null;
      buyBox.mutationObserver = null;
    }
    collectBuyBox();
  }

  let rebuildTimer = null;
  function scheduleRebuild() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(rebuildYields, 100);
  }

  function rebuildYields() {
    if (!('IntersectionObserver' in window)) return;
    yields.forEach((item) => item.rebuild());
  }

  function watchYield() {
    if (!('IntersectionObserver' in window)) return;
    window.addEventListener('resize', scheduleRebuild, { passive: true });
    // Kaufleiste kommt und geht (sticky-atc-visible, verschiebt den Chat-Knopf)
    new MutationObserver(scheduleRebuild).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // Theme-Editor tauscht Sektionen aus: Hauptsektion neu anbinden
    document.addEventListener('shopify:section:load', bindBuyBox);
    // Produktwechsel ersetzt product-info innerhalb von <main> (Dawn meldet product-info:loaded)
    document.addEventListener('product-info:loaded', bindBuyBox);
    document.addEventListener('shopify:section:unload', () => setTimeout(bindBuyBox, 0));
  }

  /* ---------- Start ---------- */
  function onBodyChange() {
    scanForms();
    styleConsent();
    placeRestock();
    // Restock Rocket kann seinen Button nachträglich einhängen, Produktwechsel ersetzt product-info
    bindBuyBox();
  }

  function init() {
    watchYield();
    onBodyChange();
    // Apps hängen ihre Elemente direkt an den body, Shadow DOM entsteht teils etwas später
    new MutationObserver(onBodyChange).observe(document.body, { childList: true });
    let tries = 0;
    const timer = setInterval(() => {
      scanForms();
      styleConsent();
      rebuildYields();
      tries += 1;
      if (tries >= 20) clearInterval(timer);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
